/**
 * GIF search result types — minimal display-only subset.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/GifSearchResult.swift`.
 *
 * ## Privacy model
 *
 * Raw provider responses (Tenor, GIPHY, …) carry a large number of tracking
 * and analytics fields: impression-tracking pixels, ad metadata, analytics
 * identifiers, and various platform-specific fields. We explicitly discard all
 * of that and keep only the fields required to render the GIF.
 *
 * The **type-level guarantee** is enforced by the shape of `GifSearchResultItem`:
 * it has NO field for `trackingPixel`, `analyticsId`, `adMetadata`, or any other
 * analytics field. Even if a provider returns those fields, `reduceProviderResult`
 * never copies them — they fall off during the reduction.
 *
 * The reducer also rewrites every media URL through the Kaether CDN proxy so
 * the upstream provider never sees the user's IP address.
 *
 * Pure logic. No `fetch()` calls. No SolidJS imports.
 */

import { PROXY_BASE_URL, validateProxyURL } from "./GifProxyPolicy";

// ---------------------------------------------------------------------------
// Display-only result type
// ---------------------------------------------------------------------------

/**
 * The minimal set of fields required to display a GIF in the picker and send
 * it as a message attachment.
 *
 * IMPORTANT — deliberately omitted fields:
 *   - `trackingPixel`  / `trackingUrls`  — no impression tracking
 *   - `analyticsId`    / `analyticsTag`  — no analytics
 *   - `adMetadata`     / `sponsored`     — no ad data
 *   - `tags`           / `categories`    — not needed for display
 *   - `created`        / `trending_datetime` — not needed for display
 *   - `shares`         / `views`         — not needed for display
 *
 * Any field added here must be strictly display-only.
 */
export interface GifSearchResultItem {
  /** Unique identifier assigned by the upstream provider. */
  readonly id: string;
  /**
   * Proxied URL of the low-quality preview (typically a small WebP or MP4).
   * Guaranteed to pass `validateProxyURL`.
   */
  readonly previewUrl: string;
  /**
   * Proxied URL of the full-quality GIF or MP4 to embed in the message.
   * Guaranteed to pass `validateProxyURL`.
   */
  readonly fullUrl: string;
  /** Width in pixels of the full-quality media. */
  readonly width: number;
  /** Height in pixels of the full-quality media. */
  readonly height: number;
}

// ---------------------------------------------------------------------------
// Raw provider shapes (internal — not exported)
// ---------------------------------------------------------------------------

/**
 * Minimal subset of a raw Tenor / GIPHY API result that we care about.
 * The reducer only reads the fields declared here; everything else (tracking
 * pixels, ad metadata, analytics IDs, …) is ignored and never stored.
 */
interface RawProviderResult {
  readonly id: string;
  /** Raw media URL pointing directly at the provider CDN. */
  readonly previewUrl: string;
  readonly fullUrl: string;
  readonly width?: number;
  readonly height?: number;
}

// ---------------------------------------------------------------------------
// URL rewriting
// ---------------------------------------------------------------------------

/**
 * Rewrites a raw upstream provider URL through the Kaether CDN GIF proxy.
 *
 * The scheme (`https://`) and host are stripped from the original URL; the
 * remainder is appended to `PROXY_BASE_URL` with a provider-namespacing
 * segment derived from the URL's hostname.
 *
 * Examples:
 *   `https://media.giphy.com/media/abc123/giphy.gif`
 *   → `https://cdn.kaether.fr/gifproxy/v1/giphy/media/abc123/giphy.gif`
 *
 *   `https://media.tenor.com/images/abc/tenor.gif`
 *   → `https://cdn.kaether.fr/gifproxy/v1/tenor/images/abc/tenor.gif`
 *
 * If the input URL is already on the proxy (starts with PROXY_BASE_URL), it
 * is returned unchanged.
 *
 * Non-HTTPS URLs are rejected — the function throws a `TypeError` so callers
 * know they have a bad input rather than silently creating an invalid proxy URL.
 *
 * @param raw - Absolute URL from the upstream provider API response.
 * @returns Absolute proxy URL that passes `validateProxyURL`.
 * @throws {TypeError} If `raw` is not an HTTPS URL.
 */
export function rewriteProviderURL(raw: string): string {
  // Already proxied — return as-is.
  if (raw.startsWith(PROXY_BASE_URL)) {
    return raw;
  }

  if (!raw.startsWith("https://")) {
    throw new TypeError(
      `GifProxyPolicy: refusing to proxy non-HTTPS URL: ${raw}`,
    );
  }

  // Strip the https:// scheme.
  const withoutScheme = raw.slice("https://".length);

  // Derive a provider namespace from the first hostname segment.
  //   media.giphy.com     → "giphy"
  //   media.tenor.com     → "tenor"
  //   i.giphy.com         → "giphy"
  //   media1.giphy.com    → "giphy"
  //   cdn.tenor.co.uk     → "tenor"
  const hostnamePart = withoutScheme.split("/")[0];
  const hostSegments = hostnamePart.split(".");
  // The second-level domain is the human-readable provider name.
  // For `media.giphy.com`  → segments = ["media","giphy","com"] → SLD = "giphy"
  // For `i.giphy.com`      → segments = ["i","giphy","com"]     → SLD = "giphy"
  // For `cdn.tenor.co.uk`  → segments = ["cdn","tenor","co","uk"] → SLD = "tenor"
  // Fallback: use the full hostname if we can't parse it.
  const providerNamespace =
    hostSegments.length >= 2
      ? hostSegments[hostSegments.length - 2]
      : hostnamePart;

  // The path after the hostname.
  const pathAfterHost = withoutScheme.slice(hostnamePart.length); // "/media/abc/…"
  const pathWithoutLeadingSlash = pathAfterHost.startsWith("/")
    ? pathAfterHost.slice(1)
    : pathAfterHost;

  const proxied = `${PROXY_BASE_URL}${providerNamespace}/${pathWithoutLeadingSlash}`;

  // Sanity check: the result must pass our own validator.
  // This should always hold given the construction above, but we assert here
  // to catch any future regression in the logic.
  if (!validateProxyURL(proxied)) {
    throw new TypeError(
      `GifProxyPolicy: internal error — constructed URL failed validation: ${proxied}`,
    );
  }

  return proxied;
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

/**
 * Converts a raw provider API result into a clean `GifSearchResultItem`.
 *
 * - Rewrites all media URLs through the proxy.
 * - Discards ALL tracking, analytics, and ad-metadata fields.
 * - Returns `null` if any required field is missing or if URL rewriting fails,
 *   so the caller can skip bad entries rather than propagating partial data.
 *
 * @param raw - Untrusted provider API object.
 * @returns Clean result, or `null` if the input is unusable.
 */
export function reduceProviderResult(
  raw: Readonly<Record<string, unknown>>,
): GifSearchResultItem | null {
  const id = typeof raw["id"] === "string" ? raw["id"] : null;
  const rawPreview =
    typeof raw["previewUrl"] === "string" ? raw["previewUrl"] : null;
  const rawFull =
    typeof raw["fullUrl"] === "string" ? raw["fullUrl"] : null;
  const width =
    typeof raw["width"] === "number" ? raw["width"] : 0;
  const height =
    typeof raw["height"] === "number" ? raw["height"] : 0;

  if (!id || !rawPreview || !rawFull) {
    return null;
  }

  try {
    return {
      id,
      previewUrl: rewriteProviderURL(rawPreview),
      fullUrl: rewriteProviderURL(rawFull),
      width,
      height,
    };
  } catch {
    return null;
  }
}

/**
 * Reduces an array of raw provider API results into clean `GifSearchResultItem`
 * values. Entries that fail reduction are silently dropped.
 *
 * @param raws - Array of untrusted provider API objects.
 */
export function reduceProviderResults(
  raws: ReadonlyArray<Readonly<Record<string, unknown>>>,
): GifSearchResultItem[] {
  const out: GifSearchResultItem[] = [];
  for (const raw of raws) {
    const item = reduceProviderResult(raw);
    if (item !== null) {
      out.push(item);
    }
  }
  return out;
}
